/**
 * usePermissions Hook
 * Provides permission checking functionality for the frontend.
 *
 * ONE REQUEST, SERVER-DECIDED (S1-m). This hook used to do two things that have now been
 * replaced by `GET /permissions/effective`:
 *
 *   1. For employee / manager / viewer it fired **27 parallel `/permissions/check` requests on
 *      every page load** — one per pair it cared about. That is 27 requests against a
 *      300-per-15-minute authenticated budget, repeated on every cold cache.
 *
 *   2. For `super_admin` and `account_admin` it **hardcoded the answer client-side** and never
 *      asked the server at all. Two copies of an authorization rule drift: the hardcoded
 *      super_admin map granted `comment:update`, which the backend denies. The server is now
 *      the only source of truth for every role.
 *
 * The map is COARSE. It cannot express ownership, so `okr:update` reads `true` for an employee
 * who may in fact only edit their own OKRs. `ownershipScoped` lists exactly those pairs. Use it
 * to decide whether to SHOW an affordance, and let the API be the authority on a specific row.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { permissionService } from '../services/permission.service';

// Permission resources and actions
export type PermissionResource =
  | 'okr'
  | 'key_result'
  | 'user'
  | 'approval'
  | 'comment'
  | 'report'
  | 'check_in'
  | 'settings'
  | 'performance';

export type PermissionAction =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'export'
  | 'configure'
  | 'create_custom'
  | 'read_team'
  | 'read_org';

interface UserPermissions {
  [key: string]: boolean; // "resource:action" -> boolean
}

/**
 * Hook to manage and check user permissions
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth();

  // One request for the caller's whole map, for EVERY role including the admins.
  const { data, isLoading } = useQuery({
    queryKey: ['effectivePermissions', user?.id, user?.role],
    queryFn: () => permissionService.getEffectivePermissions(),
    enabled: isAuthenticated && !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const permissions: UserPermissions | undefined = data?.permissions;

  /**
   * Check if the current user can perform an action on a resource.
   *
   * Denies while the map is still loading, and denies any pair the map does not mention —
   * both fail CLOSED, matching the backend, which returns false for a pair with no default and
   * no tenant row.
   */
  const can = (resource: PermissionResource, action: PermissionAction): boolean => {
    if (!isAuthenticated || !user) return false;
    if (!permissions) return false;
    return permissions[`${resource}:${action}`] ?? false;
  };

  /**
   * Whether a granted pair is additionally restricted to rows the caller owns.
   * `can()` says "the button may exist"; this says "expect a 403 on someone else's row".
   */
  const isOwnershipScoped = (resource: PermissionResource, action: PermissionAction): boolean =>
    (data?.ownershipScoped ?? []).includes(`${resource}:${action}`);

  /**
   * Check if user can create OKRs
   */
  const canCreateOKR = (): boolean => can('okr', 'create');

  /**
   * Check if user can delete OKRs
   */
  const canDeleteOKR = (): boolean => can('okr', 'delete');

  /**
   * Check if user can update OKRs
   */
  const canUpdateOKR = (): boolean => can('okr', 'update');

  /**
   * Check if user can manage users
   */
  const canManageUsers = (): boolean => can('user', 'create') || can('user', 'update') || can('user', 'delete');

  /**
   * Check if user can access settings
   */
  const canAccessSettings = (): boolean => can('settings', 'read');

  /**
   * Check if user can approve OKRs
   */
  const canApproveOKRs = (): boolean => can('approval', 'update');

  /**
   * Check if user can configure check-in schedules
   */
  const canConfigureCheckIn = (): boolean => can('check_in', 'configure');

  /**
   * Check if user can submit check-ins
   */
  const canSubmitCheckIn = (): boolean => can('check_in', 'create');

  return {
    can,
    isOwnershipScoped,
    canCreateOKR,
    canDeleteOKR,
    canUpdateOKR,
    canManageUsers,
    canAccessSettings,
    canApproveOKRs,
    canConfigureCheckIn,
    canSubmitCheckIn,
    isLoading,
    permissions,
    /** Non-null when a route guard overrides the map wholesale (super_admin, S1-j). */
    routeGuardOverride: data?.routeGuardOverride ?? null,
  };
}

export default usePermissions;
